import React, { useCallback, useEffect, useState } from "react";
import { Paintbrush, RotateCcw, ShieldCheck } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const ROWS = [
  ["template", "Template"], ["homepage_layout", "Layout beranda"], ["header_style", "Header"],
  ["footer_style", "Footer"], ["product_card_style", "Kartu produk"], ["typography_heading", "Tipografi judul"],
];
const PRESERVE_NOTE = "Produk, kategori, pesanan, pelanggan, banner, domain, dan konfigurasi bisnis tidak akan diubah.";

/* Design-Only Template Switch — kontrol Platform Owner (v1). Preview read-only → Apply (konfirmasi) → Rollback. */
export default function TenantDesignSwitchSection({ tenantId }) {
  const [templates, setTemplates] = useState([]);
  const [target, setTarget] = useState("");
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get(`/platform/tenants/${tenantId}/design-switch`);
      setStatus(r.data);
    } catch (e) { notifyError(e); }
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    loadStatus();
    api.get("/platform/template-registry", { params: { status: "PUBLISHED", limit: 100 } })
      .then((r) => setTemplates(r.data?.items || r.data || [])).catch(() => {});
  }, [tenantId, loadStatus]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); } catch (e) { notifyError(e); } finally { setBusy(false); }
  };
  const doPreview = () => run(async () => {
    const r = await api.get(`/platform/tenants/${tenantId}/design-switch/preview`, { params: { template: target } });
    setPreview(r.data);
  });
  const doApply = () => {
    if (!window.confirm(`Ganti desain tenant ini ke "${preview?.target_template?.name}"?\n\n${PRESERVE_NOTE}`)) return;
    run(async () => {
      await api.post(`/platform/tenants/${tenantId}/design-switch`, { target_template: target, token_mode: "inherit" });
      setPreview(null); await loadStatus();
    }, "Desain diganti. Konten tenant tidak berubah.");
  };
  const doRollback = () => {
    if (!window.confirm("Kembalikan desain sebelumnya? Data bisnis tidak akan diubah.")) return;
    run(async () => { await api.post(`/platform/tenants/${tenantId}/design-switch/rollback`); setPreview(null); await loadStatus(); },
      "Desain sebelumnya dipulihkan.");
  };

  const active = status?.active;
  return (
    <Card className="bg-white/5 border-white/10 p-5 rounded-2xl text-white" data-testid="design-switch-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold flex items-center gap-2"><Paintbrush size={18} /> Ganti Desain Saja</h3>
          <p className="text-xs text-white/60 mt-1 max-w-xl">{PRESERVE_NOTE}</p>
        </div>
        {active ? (
          <span className="text-xs font-semibold rounded-full px-2.5 py-1 bg-emerald-50 text-emerald-700" data-testid="design-switch-active">
            Aktif: {active.source_template_slug} (v{active.config_version})
          </span>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select value={target} onChange={(e) => { setTarget(e.target.value); setPreview(null); }}
          className="h-10 rounded-xl border border-white/15 bg-white/10 text-white px-3 text-sm min-w-[240px]" data-testid="design-switch-target">
          <option value="">Pilih template tujuan (PUBLISHED)…</option>
          {templates.map((t) => <option key={t.id} value={t.slug}>{t.name} — {t.slug}</option>)}
        </select>
        <Button variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10" onClick={doPreview} disabled={!target || busy} data-testid="design-switch-preview-btn">Preview</Button>
        <Button onClick={doApply} disabled={!preview || busy} className="brand-btn-primary" data-testid="design-switch-apply-btn">Terapkan Desain</Button>
        {active ? (
          <Button variant="ghost" className="text-white/80 hover:bg-white/10" onClick={doRollback} disabled={busy} data-testid="design-switch-rollback-btn"><RotateCcw size={14} className="mr-1" /> Rollback</Button>
        ) : null}
      </div>

      {preview ? (
        <div className="mt-4 grid gap-3 md:grid-cols-2" data-testid="design-switch-preview">
          {[["Desain Saat Ini", preview.current, "current-card"], ["Desain Tujuan", preview.target, "target-card"]].map(([title, d, k]) => (
            <div key={k} className="rounded-xl border border-white/10 p-3" data-testid={`design-switch-${k}`}>
              <div className="text-[11px] font-bold uppercase tracking-wider text-white/60 mb-2">{title}</div>
              <dl className="text-sm space-y-1">
                {ROWS.map(([key, label]) => (
                  <div key={key} className="flex justify-between gap-3"><dt className="text-white/60">{label}</dt><dd className="font-medium text-right">{d?.[key] || "—"}</dd></div>
                ))}
              </dl>
            </div>
          ))}
          <div className="md:col-span-2 rounded-xl bg-white/5 p-3 text-xs text-white/70 space-y-1" data-testid="design-switch-notice">
            <div className="flex items-center gap-1.5 font-semibold text-white"><ShieldCheck size={14} /> Tidak ada produk, kategori, banner, pesanan, atau data bisnis yang akan diimpor dari template.</div>
            <div>Warna: mode <b>inherit</b> — warna yang sudah diatur tenant ({(preview.resolved_tokens?.tenant_customized || []).join(", ") || "tidak ada"}) tetap dipakai; sisanya mengikuti default template.</div>
            <div>Dipertahankan: {preview.preserved?.counts?.products ?? 0} produk · {preview.preserved?.counts?.categories ?? 0} kategori · {preview.preserved?.counts?.banners ?? 0} banner · {preview.preserved?.counts?.orders ?? 0} pesanan · logo/nama/kontak tenant.</div>
            <div>Konten demo template yang dilewati: {preview.preserved?.demo_content_skipped?.products ?? 0} produk, {preview.preserved?.demo_content_skipped?.categories ?? 0} kategori, {preview.preserved?.demo_content_skipped?.banners ?? 0} banner.</div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
